import { InfoIcon } from "lucide-react";
import type { ServerProviderModel } from "@cmmd-forge/contracts";

import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

interface ProviderModelsListProps {
  readonly provider: string;
  readonly models: ReadonlyArray<ServerProviderModel>;
}

export function ProviderModelsList({ provider, models }: ProviderModelsListProps) {
  if (models.length === 0) return null;

  return (
    <section className="border-t border-border/60 pt-5">
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-xs font-medium text-foreground">Models</div>
        <div className="text-[11px] tabular-nums text-muted-foreground">
          {models.length} available
        </div>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto">
        {models.map((model) => (
          <ProviderModelRow key={`${provider}:${model.slug}`} model={model} />
        ))}
      </div>
    </section>
  );
}

function ProviderModelRow({ model }: { readonly model: ServerProviderModel }) {
  const capabilities = modelCapabilities(model);
  const hasDetails = capabilities.length > 0 || model.name !== model.slug;

  return (
    <div className="flex min-h-8 items-center gap-1.5 py-1">
      <span className="min-w-0 truncate text-xs text-foreground/90">{model.name}</span>
      {hasDetails ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                size="icon-xs"
                variant="ghost"
                className="text-muted-foreground/50 hover:text-muted-foreground"
                aria-label={`Details for ${model.name}`}
              />
            }
          >
            <InfoIcon className="size-3" />
          </TooltipTrigger>
          <TooltipPopup side="top" className="max-w-56">
            <div className="space-y-1">
              <code className="block text-[11px] text-foreground">{model.slug}</code>
              {capabilities.length === 0 ? null : (
                <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                  {capabilities.map((label) => (
                    <span key={label} className="text-[10px] text-muted-foreground">
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </TooltipPopup>
        </Tooltip>
      ) : null}
    </div>
  );
}

function modelCapabilities(model: ServerProviderModel): ReadonlyArray<string> {
  const labels: string[] = [];
  if (model.capabilities?.supportsFastMode) labels.push("Fast mode");
  if (model.capabilities?.supportsThinkingToggle) labels.push("Thinking");
  if ((model.capabilities?.reasoningEffortLevels?.length ?? 0) > 0) labels.push("Reasoning");
  return labels;
}
